import React, { useEffect } from "react";
import "@/App.css";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import SinglePage from "@/pages/SinglePage";
import LibraryPage from "@/pages/LibraryPage";
import AdminPage from "@/pages/AdminPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AuthCallback from "@/pages/AuthCallback";
import VersionWatcher from "@/components/VersionWatcher";
import { analyticsApi } from "@/lib/adminApi";

/**
 * Fire one visit-ping per browser session (any tab, any route). Geolocation
 * happens server-side so the client doesn't pay a key. Skipped on /admin and
 * /admin/analytics so admin testing doesn't pollute the location map.
 */
function VisitPinger() {
    const location = useLocation();
    useEffect(() => {
        const path = location.pathname || "/";
        if (path.startsWith("/admin")) return;
        if (typeof window === "undefined") return;
        try {
            if (sessionStorage.getItem("codex_visit_logged") === "1") return;
            sessionStorage.setItem("codex_visit_logged", "1");
            analyticsApi
                .visit({
                    path,
                    referrer: document.referrer || "",
                    session_id: sessionStorage.getItem("codex_session_id") || "",
                })
                .catch(() => {
                    // analytics MUST NEVER fail user flow
                });
        } catch (_e) {
            // sessionStorage can be disabled (private mode, embedded webviews)
        }
    }, []);
    return null;
}

/**
 * Synchronous OAuth fragment check — the playbook requires this to happen
 * during render (NOT inside useEffect) so the session_id is exchanged before
 * any /auth/me probe races against it.
 */
function AppRouter() {
    const location = useLocation();
    if (location.hash && location.hash.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
            <VersionWatcher />
            <VisitPinger />
            <Routes>
                <Route path="/" element={<SinglePage />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/analytics" element={<AnalyticsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <Layout>
                <AppRouter />
            </Layout>
            <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "#0A1220",
                        border: "1px solid rgba(212,175,55,0.4)",
                        color: "#E2E8F0",
                        fontFamily: "IBM Plex Sans, sans-serif",
                    },
                }}
            />
        </BrowserRouter>
    );
}
